import { AfterViewInit, Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { Subject, combineLatest } from 'rxjs';

import { JSR310ConversionService } from '../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { LocalDate } from '../../../shared/jsr310-localdates/types/local-date.type';

import { MultiSelection } from '../../../shared/checkbox-list/entities/multi-selection.entity';

import { MessagesCollationnesFilters } from '../entities/messages-collationnes-filters.entity';

import { AgenceTLR } from '../../structure/agence/entities/agence-tlr.entity';

import { AntenneService } from '../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../structure/antenne/entities/antenne-tlr.entity';

import { ParcTLR } from '../../structure/parc/entities/parc-tlr.entity';

import { UserTLR } from '../../humain/user/entities/user-tlr.entity';

@Component({
	selector: 'messages-collationnes-filters',
	templateUrl: 'messages-collationnes-filters.html',
})
export class MessagesCollationnesFiltersComponent implements AfterViewInit {
	// Elements de fonctionnements pour l'interface
	resetCommands = new Subject<void>();
	userSubject = new Subject<UserTLR>();
	
	// Indicateur pour le verouillage de l'agence et antenne
	userIsExp!: boolean;

	// Valeurs des filtres choisis par l'utilisateur
	filters: MessagesCollationnesFilters = new MessagesCollationnesFilters();

	constructor( //
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
		@Inject(AntenneService) antenneService: AntenneService,
	) {
		// Chargement des antennes
		let antennes$ = antenneService.getAntenneTLRMapById();
		
		// Une fois toutes les informations de contexte disponible, et a chaque fois qu'un demande de (re-)initialisation est faite
		// - Remettre les filtres a leur valeur par defaut
		// - Relancer la requete de filtrage aupres du parent
		combineLatest([this.userSubject, antennes$, this.resetCommands]).subscribe(
			(data) => {
				let user = data[0];
				let antennes = data[1];

				this.filters = new MessagesCollationnesFilters();
				if (user.antenneId) {
					let antenne = antennes.get(user.antenneId);
					if (!antenne) {
						throw new Error("Antenne #" + user.antenneId + " introuvable dans le contexte");
					}
					this.filters.agenceId = antenne.agenceId;	
					this.filters.antenneId = user.antenneId;
				}
				
				// Fixer la date minimum au premier jour du mois en cours
				let today = new Date();
				let startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
				this.filters.dateMin = this.conversionService.toMandatoryLocalDate(startOfMonth);
				
				// Fixer la date maximum au dernier jour du mois en courd
				let endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
				this.filters.dateMax = this.conversionService.toMandatoryLocalDate(endOfMonth);
				
				// Emettre l'evenement de changement de filtres
				this.filterRequest.emit(this.filters);
			}
		)
	}
	
	// Reception de l'utilisateur connecte depuis le parent
	@Input() set user(user: UserTLR | undefined) {
		if (user) {
			this.userIsExp = user.role === "EXP";
			this.userSubject.next(user);
		}
	}

	// Evenement pour quand l'utilisateur demande l'application des filtres
	@Output() filterRequest = new EventEmitter<MessagesCollationnesFilters>();

	ngAfterViewInit() {
		// NB: Attention! Comme resetCommands est potentiellement bind sur les sous-composants,
		// il faut attendre qu'il ait ete transmis avant d'envoyer le premier signal d'initialisation
		this.resetCommands.next();
	}

	onDateDebutChange(value: LocalDate | null) {
		this.filters.dateMin = value;
	}

	onDateFinChange(value: LocalDate | null) {
		this.filters.dateMax = value;
	}
	
	onAgenceChange(agence: AgenceTLR | null) {
		if (agence) {
			if (agence.id != this.filters.agenceId) {
				// on a changé d'agence
				this.filters.agenceId = agence.id;
				// on vide l'antenne et le parc
				delete this.filters.antenneId;
				delete this.filters.parcId;
			}
		} else {
			// on a cliquer sur moins pour enlever l'agence choisi
			delete this.filters.agenceId;
			delete this.filters.antenneId;
			delete this.filters.parcId;
		}
	}
	
	onAntenneChange(antenne: AntenneTLR | null) {
		if (antenne) {
			if (this.filters.agenceId === undefined) {
				// on a choisis un parc une antenne mais pas d'agence
				this.filters.agenceId = antenne.agenceId;
			}
			if (antenne.id != this.filters.antenneId) {
				// on a changé d'antenne, on vide le parc
				this.filters.antenneId = antenne.id;
				delete this.filters.parcId;
			}			
		} else {
			// on a cliquer sur moins pour enlever l'antenne choisi
			delete this.filters.antenneId;
			delete this.filters.parcId;
		}
	}
	
	onParcChange(parc: ParcTLR | null) {
		if (parc) {
			this.filters.parcId = parc.id;
			if (this.filters.antenneId === undefined) {
				this.filters.antenneId = parc.antenneId;
			}
		} else {
			delete this.filters.parcId;
		}
	}

	onSelectedERChange(value: MultiSelection | undefined) {
		this.filters.emetteur = value;
	}
	
	onSelectedEtatParcChange(value: MultiSelection | undefined) {
		this.filters.etatParc = value;
	}

	resetFilters() {
		this.resetCommands.next();
	}

	search() {
		this.filterRequest.emit(this.filters);
	}
}